import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmationDialog from './ConfirmationDialog'
import { PermissionsContext } from '../../app/PermissionsContext'
import {
  downloadUserGedcomResultsErrors,
  reprocessGedcomFileUpload,
  deleteGedcomFileUpload,
} from '../../../services/gedcomService'
import { getGroupSummary, updateGroupProperties } from '../../../services/groupsService'
import { downloadFile } from '../../../util/FileDownloadUtil'
import useNotifications from '../../../hooks/useNotifications'

const deleteGroup = async (file, fetchGedcomData, error) => {
  let groupResponse
  try {
    groupResponse = await getGroupSummary(file.groupId)
  } catch (err) {
    error(
      `Failed to delete group ${file.groupId} for GEDCOM upload ${file.fileName} for user ${
        file.contributor
      }. Error: ${JSON.stringify(err)}`
    )
    fetchGedcomData()
    return
  }
  let group = groupResponse.data
  group = { ...group, tombstoned: true }
  try {
    await updateGroupProperties(file.groupId, group)
  } catch {
    error(`Failed to tombstone group ${file.groupId} for gedcom upload ${file.fileName}.`)
  }

  // Tree deletion logic placeholder
  fetchGedcomData()
}

const UserGedcomActionColumn = ({ file, cisId, fetchGedcomData }) => {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { isRole } = useContext(PermissionsContext)
  const canReprocessOrDelete = isRole(['Engineer Admin', 'Data Admin 5', 'Data Admin 6'])
  return (
    <>
      <ConfirmationDialog
        title={t('user.gedcom.downloadTitle', 'Download Gedcom File Results')}
        btnLabel={t('user.gedcom.resultsButton', 'Results')}
        message={
          <div>
            {t('user.gedcom.downloadConfirmationMessage', 'Download results file for GEDCOM upload')}
            <b>
              &nbsp;{file.fileName} ({file.title})&nbsp;
            </b>{' '}
            {t('user.gedcom.forUser', 'for user')} <b>{file.contributor}</b>?
          </div>
        }
        confirmBtnOnClick={async () => {
          const response = await downloadUserGedcomResultsErrors(cisId, file.fileName)
          const responseFile = new Blob([JSON.stringify(response.data, null, 2)], { type: 'plain/text' })
          downloadFile(responseFile, `${cisId}_${file.fileName}_results.txt`)
        }}
      />
      {canReprocessOrDelete && (
        <>
          <br />
          <ConfirmationDialog
            title={t('user.gedcom.reprocessTitle', 'Reprocess GEDCOM File')}
            btnLabel={t('user.gedcom.reprocessButton', 'Reprocess')}
            message={
              <div>
                {t('user.gedcom.reprocessConfirmationMessage', 'Are you sure you want to reprocess GEDCOM upload')}
                <b>
                  &nbsp;{file.fileName} ({file.title})&nbsp;
                </b>{' '}
                {t('user.gedcom.forUser', 'for user')} <b>{file.contributor}</b>?
              </div>
            }
            confirmBtnOnClick={async () => {
              await reprocessGedcomFileUpload(cisId, file.fileName)
              fetchGedcomData()
            }}
          />
          <br />
          <ConfirmationDialog
            isDanger
            extra
            title={t('user.gedcom.deleteConfirmationTitle', 'Delete GEDCOM File')}
            message={
              <div>
                {t('user.gedcom.deleteConfirmationMessage', 'Are you sure you want to delete GEDCOM upload')}
                <b>
                  &nbsp;{file.fileName} ({file.title})&nbsp;
                </b>
                {t('user.gedcom.forUser', 'for user')} <b>{file.contributor}</b>?
              </div>
            }
            btnLabel={t('action.delete', 'Delete')}
            confirmBtnOnClick={async (shouldDeleteTree, shouldDeleteGroup) => {
              try {
                await deleteGedcomFileUpload(cisId, file.fileName)
              } catch (err) {
                error(
                  `Failed to delete GEDCOM upload ${file.fileName} for user ${file.contributor}. Error: ${JSON.stringify(
                    err
                  )}`
                )
              }

              if (shouldDeleteGroup) {
                await deleteGroup(file, fetchGedcomData, error)
              }
            }}
          />
        </>
      )}
    </>
  )
}

export default UserGedcomActionColumn
